import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  IconButton,
  Avatar,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack,
  LocationOn,
  DirectionsCar,
  Person,
  Assignment,
  SupportAgent,
  Description,
  EditNote,
  CheckCircle,
  CheckCircleOutline,
  CancelOutlined,
  HourglassEmpty,
  ChevronLeft,
  ChevronRight,
  DeleteOutline,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { useRequestState } from "@app/hooks";
import { PATH_DASHBOARD } from "@app/routes/paths";
import {
  selectRequestDetailState,
  selectApproveSellCarState,
  selectDeleterequestState,
} from "../selector";
import { requestDetails, approveSellCar, deleteRequest } from "../slice";
import { Strings } from "@app/constants";
import { IRequestDetailItem } from "@app/types/request";

import { useTheme, alpha } from "@mui/material/styles";

// ----------------------------------------------------------------------
// Helper Formatters
// ----------------------------------------------------------------------
const formatDate = (dateString?: string | null) => {
  if (!dateString) return "N/A";
  const d = dayjs(dateString);
  if (!d.isValid()) return String(dateString);
  return d.format("DD MMM YYYY, hh:mm A");
};

const formatDateOnly = (dateString?: string | null) => {
  if (!dateString) return "N/A";
  const d = dayjs(dateString);
  if (!d.isValid()) return String(dateString);
  return d.format("DD MMM YYYY");
};

const formatCurrency = (amount?: number | string | null) => {
  if (amount === undefined || amount === null || amount === "") return "N/A";
  const num = Number(amount);
  if (isNaN(num)) return String(amount);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(num);
};

const formatEnumText = (text?: string | null) => {
  if (!text) return "N/A";
  return text
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

// ----------------------------------------------------------------------
// Styled Status Badge
// ----------------------------------------------------------------------
const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const theme = useTheme();
  const s = (status || "Pending").toLowerCase();

  let bg = theme.palette.warning.lighter;
  let color = theme.palette.warning.darker;
  let icon = <HourglassEmpty sx={{ fontSize: 15 }} />;

  if (s === "approved" || s === "completed" || s === "success") {
    bg = theme.palette.success.lighter;
    color = theme.palette.success.darker;
    icon = <CheckCircleOutline sx={{ fontSize: 15 }} />;
  } else if (s === "assigned") {
    bg = theme.palette.info.lighter;
    color = theme.palette.info.darker;
    icon = <Assignment sx={{ fontSize: 15 }} />;
  } else if (s === "rejected" || s === "cancelled") {
    bg = theme.palette.error.lighter;
    color = theme.palette.error.darker;
    icon = <CancelOutlined sx={{ fontSize: 15 }} />;
  }

  return (
    <Chip
      icon={icon}
      label={status || "Pending"}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.8rem",
        backgroundColor: bg,
        color: color,
        "& .MuiChip-icon": { color: `${color} !important` },
      }}
    />
  );
};

// ----------------------------------------------------------------------
// Styled Type Badge
// ----------------------------------------------------------------------
const TypeBadge: React.FC<{ type?: string }> = ({ type }) => {
  const theme = useTheme();
  const t = type || "Car Evaluation";

  return (
    <Chip
      label={t}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.8rem",
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.primary,
        borderRadius: 1,
      }}
    />
  );
};

// ----------------------------------------------------------------------
// Clean Detail Row Component
// ----------------------------------------------------------------------
interface DetailRowProps {
  label: string;
  value: React.ReactNode;
  isLink?: "tel" | "mailto";
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, isLink }) => {
  const theme = useTheme();
  const isEmpty =
    value === null ||
    value === undefined ||
    value === "" ||
    value === "N/A" ||
    value === "null";

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: 1.25,
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: theme.palette.text.secondary,
          fontSize: "0.875rem",
          minWidth: 160,
        }}
      >
        {label} :
      </Typography>

      <Box sx={{ maxWidth: "65%", textAlign: "right" }}>
        {isLink && !isEmpty && typeof value === "string" ? (
          <Box
            component="a"
            href={isLink === "tel" ? `tel:${value}` : `mailto:${value}`}
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
              fontSize: "0.875rem",
              textDecoration: "none",
              wordBreak: "break-word",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {value}
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: isEmpty
                ? theme.palette.text.disabled
                : theme.palette.text.primary,
              fontSize: "0.875rem",
              wordBreak: "break-word",
            }}
          >
            {isEmpty ? "N/A" : value}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

// ----------------------------------------------------------------------
// Section Card Component
// ----------------------------------------------------------------------
interface ModernCardProps {
  title: string;
  icon: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}

const ModernCard: React.FC<ModernCardProps> = ({
  title,
  icon,
  action,
  children,
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 1.5,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          pb: 1.5,
          mb: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              width: 36,
              height: 36,
              borderRadius: 1,
            }}
          >
            {icon}
          </Avatar>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              fontSize: "1rem",
            }}
          >
            {title}
          </Typography>
        </Stack>
        {action}
      </Stack>
      <Box>{children}</Box>
    </Paper>
  );
};

// ----------------------------------------------------------------------
// Main RequestDetailPage Component
// ----------------------------------------------------------------------
export const RequestDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [activeTab, setActiveTab] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const { data, loading } = useRequestState({
    stateSelector: selectRequestDetailState,
    errorShown: true,
  });

  const { enqueueSnackbar } = useSnackbar();

  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { loading: approveLoading } = useRequestState({
    stateSelector: selectApproveSellCarState,
    successMessageShown: true,
    errorShown: true,
    onSuccess: () => {
      setOpenApproveModal(false);
    },
  });

  const { loading: isDeleting } = useRequestState({
    stateSelector: selectDeleterequestState,
    successMessageShown: true,
    errorShown: true,
    onSuccess: () => {
      setOpenDeleteModal(false);
      navigate(PATH_DASHBOARD.requests);
    },
  });

  useEffect(() => {
    if (id) {
      dispatch(requestDetails({ id: Number(id) }));
    }
  }, [dispatch, id]);

  const request: IRequestDetailItem = (data?.data ||
    data ||
    {}) as IRequestDetailItem;
  const user = request?.user || null;
  const car = request?.car || null;
  const sellCar = request?.sellCar || null;
  const assignedSalesperson = request?.assignedSalesperson || null;
  const assignedAgent = request?.assignedAgent || null;
  const approvedBy = request?.approvedBy || null;

  const hasAgent = Boolean(
    assignedAgent &&
    (assignedAgent.id ||
      assignedAgent.firstName ||
      assignedAgent.lastName ||
      assignedAgent.name ||
      assignedAgent.email ||
      assignedAgent.phone ||
      (request as any).assignedAgentId ||
      (request as any).agentId),
  );

  const isSellType = (request?.type || "").toLowerCase() === "sell";
  const statusStr = (request?.status || "").toLowerCase();
  const isPending = statusStr === "pending";
  const isApproved = statusStr === "approved" || statusStr === "completed";
  const isApproveDisabled = isPending || isApproved || approveLoading;

  const hasSalesperson = Boolean(
    assignedSalesperson &&
    (assignedSalesperson.id ||
      assignedSalesperson.firstName ||
      assignedSalesperson.lastName ||
      assignedSalesperson.name ||
      assignedSalesperson.email ||
      assignedSalesperson.phone),
  );

  const assignmentType =
    hasAgent && hasSalesperson
      ? "Agent & Salesperson"
      : hasAgent
        ? assignedAgent?.role || "Agent"
        : hasSalesperson
          ? assignedSalesperson?.role || "Salesperson"
          : "Not Assigned";

  const approvedByName =
    typeof approvedBy === "object" && approvedBy !== null
      ? [approvedBy.firstName, approvedBy.lastName].filter(Boolean).join(" ") ||
      approvedBy.name ||
      null
      : typeof approvedBy === "string"
        ? approvedBy
        : null;

  const fontFamily = Strings.fontLanguage.workSans || "'Work Sans', sans-serif";

  // Compute Customer Name
  const customerName =
    [(request?.firstName || user?.firstName), (request?.lastName || user?.lastName)].filter(Boolean).join(" ") ||
    [request?.firstName, request?.lastName].filter(Boolean).join(" ") ||
    "Customer";

  // Compute User initials
  const userInitials =
    [user?.firstName?.[0], user?.lastName?.[0]]
      .filter(Boolean)
      .join("")
      .toUpperCase() ||
    customerName.slice(0, 2).toUpperCase() ||
    "U";

  // Compute Contact Name
  const contactName =
    [request?.firstName, request?.lastName].filter(Boolean).join(" ") ||
    customerName;

  // Car images resolution
  const carImages: string[] = [];
  if (car?.images && Array.isArray(car.images)) {
    car.images.forEach((img) => {
      if (typeof img === "string" && img) carImages.push(img);
      else if (img && typeof img === "object" && img.url)
        carImages.push(img.url);
    });
  }
  if (sellCar?.images && Array.isArray(sellCar.images)) {
    sellCar.images.forEach((img) => {
      if (typeof img === "string" && img) carImages.push(img);
      else if (img && typeof img === "object" && img.url)
        carImages.push(img.url);
    });
  }

  // Active car brand & model
  const carBrandName =
    (typeof car?.brand === "object" ? car?.brand?.name : car?.brand) ||
    (typeof sellCar?.brand === "object"
      ? sellCar?.brand?.name
      : sellCar?.brand) ||
    "";

  const carModelName =
    (typeof car?.model === "object" ? car?.model?.name : car?.model) ||
    (typeof sellCar?.model === "object"
      ? sellCar?.model?.name
      : sellCar?.model) ||
    "";

  const carMfgYear =
    car?.manufacturingYear ||
    sellCar?.manufacturingYear ||
    "";

  // Car Price / Valuation
  const carPrice =
    sellCar?.price ||
    car?.price ||
    request?.valuation ||
    request?.price ||
    request?.totalAmount ||
    sellCar?.expectedPrice ||
    car?.expectedPrice ||
    null;

  const hasPrice = Boolean(
    carPrice !== null &&
    carPrice !== undefined &&
    carPrice !== "" &&
    Number(carPrice) > 0
  );

  const handleApproveClick = () => {
    if (!hasPrice) {
      enqueueSnackbar(
        "Please add the car valuation/price before approving this request.",
        { variant: "warning" }
      );
      return;
    }
    setOpenApproveModal(true);
  };

  const handleApproveSubmit = () => {
    if (!hasPrice) {
      enqueueSnackbar(
        "Please add the car valuation/price before approving this request.",
        { variant: "warning" }
      );
      setOpenApproveModal(false);
      return;
    }
    if (id) {
      dispatch(approveSellCar({ id: Number(id) }));
    }
  };

  // Car Number / License Plate Number
  const carNumber =
    request?.carNumber ||
    request?.car_number ||
    request?.licensePlateNumber ||
    request?.registrationNumber ||
    car?.carNumber ||
    sellCar?.carNumber ||
    car?.licensePlateNumber ||
    sellCar?.licensePlateNumber ||
    car?.registrationNumber ||
    sellCar?.registrationNumber ||
    "";

  const reqType = (request?.type || "").trim().toLowerCase();
  const isCarEvaluation =
    reqType === "car evaluation" ||
    reqType === "car-evaluation" ||
    reqType === "car_evaluation" ||
    reqType === "evaluation";

  if (loading && !request?.id) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.paper,
        minHeight: "100vh",
        fontFamily,
      }}
    >
      {/* ------------------------------------------------------------- */}
      {/* HEADER SECTION */}
      {/* ------------------------------------------------------------- */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {/* Back Button + Title + Badges */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <ArrowBack sx={{ fontSize: 20 }} />
          </IconButton>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ rowGap: 1 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                fontSize: { xs: "1.25rem", sm: "1.4rem" },
                mr: 0.5,
              }}
            >
              Request Details
            </Typography>
            <Chip
              label={request?.requestId || `REQ #${id}`}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.text.primary,
                fontFamily: "monospace",
                fontSize: "0.82rem",
                height: 26,
              }}
            />
            <StatusBadge status={request?.status} />
            <TypeBadge type={request?.type} />
          </Stack>
        </Stack>

        {/* Header Action Buttons */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Delete Request Button */}
          <Button
            variant="outlined"
            startIcon={<DeleteOutline />}
            onClick={() => setOpenDeleteModal(true)}
            sx={{
              textTransform: "none",
              fontFamily,
              fontWeight: 600,
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
              px: 2,
              py: 0.8,
              borderRadius: 1.5,
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: theme.palette.error.dark,
                backgroundColor: alpha(theme.palette.error.main, 0.04),
              },
            }}
          >
            Delete
          </Button>

          {/* Approve Button: Appears when request type is sell and agent is assigned */}
          {isSellType && hasAgent && (
            <Tooltip
              title={
                isPending
                  ? "Status is pending. Please update status before approving."
                  : !hasPrice
                    ? "Please add the car valuation/price before approving this request."
                    : isApproved
                      ? "This request has already been approved."
                      : ""
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={isApproved ? <CheckCircleOutline /> : <CheckCircle />}
                  disabled={isApproveDisabled}
                  onClick={handleApproveClick}
                  sx={{
                    textTransform: "none",
                    fontFamily,
                    fontWeight: 600,
                    backgroundColor: isApproved ? theme.palette.success.main : theme.palette.primary.main,
                    color: "white",
                    px: 2.5,
                    py: 0.9,
                    borderRadius: 1.5,
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: isApproved ? theme.palette.success.dark : theme.palette.primary.dark,
                    },
                  }}
                >
                  {isApproved ? "Approved" : "Approve"}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* ------------------------------------------------------------- */}
      {/* 3 SUMMARY STATS TILES */}
      {/* ------------------------------------------------------------- */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(3, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 1,
            }}
          >
            <Assignment sx={{ fontSize: 20 }} />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
                display: "block",
              }}
            >
              Request Type
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                wordBreak: "break-word",
                whiteSpace: "normal",
                lineHeight: 1.3,
              }}
            >
              {request?.type || "Car Evaluation"}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 1,
            }}
          >
            <DirectionsCar sx={{ fontSize: 20 }} />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
                display: "block",
              }}
            >
              Vehicle / Model
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                wordBreak: "break-word",
                whiteSpace: "normal",
                lineHeight: 1.3,
              }}
            >
              {(carBrandName && `${carBrandName} ${carModelName}`.trim()) ||
                carNumber ||
                "Not Specified"}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 1,
            }}
          >
            <LocationOn sx={{ fontSize: 20 }} />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
                display: "block",
              }}
            >
              Service Location
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                wordBreak: "break-word",
                whiteSpace: "normal",
                lineHeight: 1.3,
              }}
            >
              {[request?.city || user?.city, request?.state || user?.state]
                .filter(Boolean)
                .join(", ") || "Not Provided"}
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* ------------------------------------------------------------- */}
      {/* NAVIGATION TABS */}
      {/* ------------------------------------------------------------- */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 1,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          p: 0.5,
          mb: 3,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            "& .MuiTabs-flexContainer": {
              gap: 0.5,
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontFamily: fontFamily,
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
              minHeight: 40,
              minWidth: isMobile ? "auto" : 0,
              py: 0.8,
              px: { xs: 1.5, sm: 1.5, md: 1 },
              borderRadius: 1,
              color: theme.palette.text.secondary,
              whiteSpace: "nowrap",
              flexShrink: 0,
              transition: "all 0.2s ease",
              "& .MuiTab-iconWrapper": {
                mr: 0.8,
                mb: "0px !important",
              },
              "&.Mui-selected": {
                color: theme.palette.primary.main,
                fontWeight: 700,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.primary.main,
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab
            icon={<Description sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label="Request Info"
          />
          <Tab
            icon={<DirectionsCar sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label="Vehicle Details"
          />
          <Tab
            icon={<Person sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label="Customer Profile"
          />
          <Tab
            icon={<LocationOn sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label="Contact & Location"
          />
          <Tab
            icon={<SupportAgent sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label="Assignment Details"
          />
        </Tabs>
      </Paper>

      {/* ------------------------------------------------------------- */}
      {/* TAB PANELS */}
      {/* ------------------------------------------------------------- */}
      <Box>
        {/* TAB 0: REQUEST INFO */}
        {activeTab === 0 && (
          <ModernCard title="Request Information" icon={<Description />}>
            <DetailRow
              label="Request ID"
              value={request?.requestId || `REQ #${id}`}
            />
            <DetailRow
              label="Request Type"
              value={<TypeBadge type={request?.type} />}
            />
            <DetailRow
              label="Status"
              value={<StatusBadge status={request?.status} />}
            />
            <DetailRow
              label="Date"
              value={
                request?.date
                  ? formatDateOnly(request.date)
                  : formatDateOnly(request?.createdAt)
              }
            />
            {request?.time && <DetailRow label="Time" value={request.time} />}

            {request?.notes && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.warning.main, 0.08),
                  border: `1px solid ${theme.palette.warning.lighter}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.warning.darker,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.5,
                    fontSize: "0.85rem",
                  }}
                >
                  <EditNote sx={{ fontSize: 18 }} /> Notes:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, lineHeight: 1.5 }}
                >
                  {request.notes}
                </Typography>
              </Box>
            )}
          </ModernCard>
        )}

        {/* TAB 1: VEHICLE DETAILS */}
        {activeTab === 1 && (
          <ModernCard title="Vehicle Details" icon={<DirectionsCar />}>
            {isCarEvaluation ? (
              <>
                <DetailRow label="Car Number" value={carNumber || "N/A"} />
                <DetailRow
                  label="Car Price / Value"
                  value={
                    carPrice ? (
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.success.main,
                        }}
                      >
                        {formatCurrency(carPrice)}
                      </Typography>
                    ) : (
                      "N/A"
                    )
                  }
                />
              </>
            ) : car || sellCar || carNumber ? (
              <>
                {/* Car Images Gallery */}
                {carImages.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        width: "100%",
                        height: { xs: 240, sm: 320, md: 360 },
                        borderRadius: 1.5,
                        overflow: "hidden",
                        backgroundColor: theme.palette.grey[50],
                        border: `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        p: 2,
                      }}
                    >
                      <Box
                        component="img"
                        src={carImages[selectedImageIndex] || carImages[0]}
                        alt="Vehicle"
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          width: "auto",
                          height: "auto",
                          objectFit: "contain",
                          transition: "all 0.3s ease",
                        }}
                      />

                      {/* Next / Prev Navigation Buttons */}
                      {carImages.length > 1 && (
                        <>
                          <IconButton
                            onClick={() =>
                              setSelectedImageIndex((prev) =>
                                prev > 0 ? prev - 1 : carImages.length - 1,
                              )
                            }
                            sx={{
                              position: "absolute",
                              left: 12,
                              top: "50%",
                              transform: "translateY(-50%)",
                              backgroundColor: alpha(
                                theme.palette.common.white,
                                0.9,
                              ),
                              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                              color: theme.palette.text.primary,
                              "&:hover": {
                                backgroundColor: theme.palette.common.white,
                              },
                            }}
                            size="small"
                          >
                            <ChevronLeft sx={{ fontSize: 22 }} />
                          </IconButton>

                          <IconButton
                            onClick={() =>
                              setSelectedImageIndex((prev) =>
                                prev < carImages.length - 1 ? prev + 1 : 0,
                              )
                            }
                            sx={{
                              position: "absolute",
                              right: 12,
                              top: "50%",
                              transform: "translateY(-50%)",
                              backgroundColor: alpha(
                                theme.palette.common.white,
                                0.9,
                              ),
                              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                              color: theme.palette.text.primary,
                              "&:hover": {
                                backgroundColor: theme.palette.common.white,
                              },
                            }}
                            size="small"
                          >
                            <ChevronRight sx={{ fontSize: 22 }} />
                          </IconButton>
                        </>
                      )}

                      {/* Counter Badge */}
                      {carImages.length > 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 12,
                            right: 12,
                            backgroundColor: alpha(
                              theme.palette.grey[900],
                              0.75,
                            ),
                            color: theme.palette.common.white,
                            px: 1.2,
                            py: 0.4,
                            borderRadius: 1,
                            fontSize: "0.78rem",
                            fontWeight: 600,
                          }}
                        >
                          {selectedImageIndex + 1} / {carImages.length}
                        </Box>
                      )}
                    </Box>

                    {/* Thumbnail Strip */}
                    {carImages.length > 1 && (
                      <Stack
                        direction="row"
                        spacing={1.5}
                        sx={{
                          mt: 1.5,
                          overflowX: "auto",
                          pb: 0.5,
                          "&::-webkit-scrollbar": { height: 4 },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: theme.palette.divider,
                            borderRadius: 2,
                          },
                        }}
                      >
                        {carImages.map((imgUrl, idx) => (
                          <Box
                            key={idx}
                            onClick={() => setSelectedImageIndex(idx)}
                            sx={{
                              width: 72,
                              height: 52,
                              borderRadius: 1,
                              overflow: "hidden",
                              cursor: "pointer",
                              backgroundColor: theme.palette.grey[50],
                              border:
                                selectedImageIndex === idx
                                  ? `2px solid ${theme.palette.primary.main}`
                                  : `1px solid ${theme.palette.divider}`,
                              opacity: selectedImageIndex === idx ? 1 : 0.6,
                              transition: "all 0.2s ease",
                              p: 0.3,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              "&:hover": { opacity: 1 },
                            }}
                          >
                            <Box
                              component="img"
                              src={imgUrl}
                              alt={`Thumbnail ${idx + 1}`}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}

                {/* All Vehicle Detail Rows in Clean List - Only show present fields */}
                {carNumber && (
                  <DetailRow label="Car Number" value={carNumber} />
                )}
                {carBrandName && (
                  <DetailRow label="Brand" value={carBrandName} />
                )}
                {carModelName && (
                  <DetailRow label="Car Model" value={carModelName} />
                )}
                {(car?.variant || sellCar?.variant) && (
                  <DetailRow
                    label="Variant"
                    value={car?.variant || sellCar?.variant}
                  />
                )}
                {(car?.carCondition || sellCar?.carCondition) && (
                  <DetailRow
                    label="Vehicle Condition"
                    value={formatEnumText(
                      car?.carCondition || sellCar?.carCondition,
                    )}
                  />
                )}
                {(car?.usageType || sellCar?.usageType) && (
                  <DetailRow
                    label="Usage Type"
                    value={formatEnumText(
                      car?.usageType || sellCar?.usageType,
                    )}
                  />
                )}
                {(car?.rcAvailable !== undefined ||
                  sellCar?.rcAvailable !== undefined) &&
                  (car?.rcAvailable !== null ||
                    sellCar?.rcAvailable !== null) && (
                    <DetailRow
                      label="RC Available"
                      value={
                        (car?.rcAvailable ?? sellCar?.rcAvailable)
                          ? "Yes"
                          : "No"
                      }
                    />
                  )}
                {(car?.insuranceAvailable !== undefined ||
                  sellCar?.insuranceAvailable !== undefined) &&
                  (car?.insuranceAvailable !== null ||
                    sellCar?.insuranceAvailable !== null) && (
                    <DetailRow
                      label="Insurance Available"
                      value={
                        (car?.insuranceAvailable ??
                          sellCar?.insuranceAvailable)
                          ? "Yes"
                          : "No"
                      }
                    />
                  )}
                {(car?.insuranceType || sellCar?.insuranceType) && (
                  <DetailRow
                    label="Insurance Type"
                    value={formatEnumText(
                      car?.insuranceType || sellCar?.insuranceType,
                    )}
                  />
                )}
                {(car?.insuranceValidTill ||
                  sellCar?.insuranceValidTill) && (
                    <DetailRow
                      label="Insurance Valid Till"
                      value={formatDateOnly(
                        car?.insuranceValidTill ||
                        sellCar?.insuranceValidTill,
                      )}
                    />
                  )}
                {(car?.anyClaimsMade !== undefined ||
                  sellCar?.anyClaimsMade !== undefined) &&
                  (car?.anyClaimsMade !== null ||
                    sellCar?.anyClaimsMade !== null) && (
                    <DetailRow
                      label="Claims Made"
                      value={
                        (car?.anyClaimsMade ?? sellCar?.anyClaimsMade)
                          ? "Yes"
                          : "No"
                      }
                    />
                  )}
                {(car?.accidentHistory || sellCar?.accidentHistory) && (
                  <DetailRow
                    label="Accident History"
                    value={formatEnumText(
                      car?.accidentHistory || sellCar?.accidentHistory,
                    )}
                  />
                )}
                {(car?.serviceHistory !== undefined ||
                  sellCar?.serviceHistory !== undefined) &&
                  (car?.serviceHistory !== null ||
                    sellCar?.serviceHistory !== null) && (
                    <DetailRow
                      label="Service History"
                      value={
                        (car?.serviceHistory ?? sellCar?.serviceHistory)
                          ? "Yes"
                          : "No"
                      }
                    />
                  )}
                {(car?.loanHypothecation !== undefined ||
                  sellCar?.loanHypothecation !== undefined) &&
                  (car?.loanHypothecation !== null ||
                    sellCar?.loanHypothecation !== null) && (
                    <DetailRow
                      label="Loan / Hypothecation"
                      value={
                        (car?.loanHypothecation ??
                          sellCar?.loanHypothecation)
                          ? "Yes"
                          : "No"
                      }
                    />
                  )}
                {(car?.manufacturingYear ||
                  car?.year ||
                  sellCar?.manufacturingYear ||
                  sellCar?.year) && (
                    <DetailRow
                      label="Manufacturing Year"
                      value={String(
                        car?.manufacturingYear ||
                        car?.year ||
                        sellCar?.manufacturingYear ||
                        sellCar?.year,
                      )}
                    />
                  )}
                {(car?.engineType ||
                  car?.fuelType ||
                  sellCar?.fuelType ||
                  sellCar?.engineType) && (
                    <DetailRow
                      label="Engine / Fuel Type"
                      value={formatEnumText(
                        car?.engineType ||
                        car?.fuelType ||
                        sellCar?.fuelType ||
                        sellCar?.engineType,
                      )}
                    />
                  )}
                {(car?.transmission || sellCar?.transmission) && (
                  <DetailRow
                    label="Transmission"
                    value={formatEnumText(
                      car?.transmission || sellCar?.transmission,
                    )}
                  />
                )}
                {(car?.driven ||
                  sellCar?.driven ||
                  car?.kilometers ||
                  sellCar?.kilometers) && (
                    <DetailRow
                      label="Kilometers Driven"
                      value={(() => {
                        const d =
                          car?.driven ??
                          sellCar?.driven ??
                          car?.kilometers ??
                          sellCar?.kilometers;
                        const num = Number(d);
                        if (!isNaN(num) && typeof d !== "boolean") {
                          return `${num.toLocaleString("en-IN")} km`;
                        }
                        const s = String(d).trim();
                        return s.toLowerCase().includes("km") ? s : `${s} km`;
                      })()}
                    />
                  )}
                {(car?.color || sellCar?.color) && (
                  <DetailRow
                    label="Color"
                    value={formatEnumText(car?.color || sellCar?.color)}
                  />
                )}
                {(car?.ownership || sellCar?.ownership) && (
                  <DetailRow
                    label="Ownership"
                    value={formatEnumText(
                      car?.ownership || sellCar?.ownership,
                    )}
                  />
                )}
                {(car?.sellingTime || sellCar?.sellingTime) && (
                  <DetailRow
                    label="Selling Time"
                    value={formatEnumText(
                      car?.sellingTime || sellCar?.sellingTime,
                    )}
                  />
                )}
                {(car?.description || sellCar?.description) && (
                  <DetailRow
                    label="Description"
                    value={car?.description || sellCar?.description}
                  />
                )}
                {(car?.price || sellCar?.expectedPrice) && (
                  <DetailRow
                    label="Car Price / Value"
                    value={
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.success.main,
                        }}
                      >
                        {formatCurrency(
                          car?.price || sellCar?.expectedPrice,
                        )}
                      </Typography>
                    }
                  />
                )}
              </>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3, textAlign: "center" }}
              >
                No vehicle details attached to this request.
              </Typography>
            )}
          </ModernCard>
        )}

        {/* TAB 2: CUSTOMER PROFILE */}
        {activeTab === 2 && (
          <ModernCard
            title="Customer Profile"
            icon={<Person />}
            action={
              user?.isActive !== undefined && (
                <Chip
                  label={user.isActive ? "Active" : "Inactive"}
                  size="small"
                  color={user.isActive ? "success" : "default"}
                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                />
              )
            }
          >
            {user?.id ? (
              <>
                <DetailRow label="Customer ID" value={`#${user.id}`} />
                <DetailRow label="Customer Name" value={customerName} />
                <DetailRow
                  label="Email"
                  value={request?.email || user?.email || "N/A"}
                  isLink="mailto"
                />
                <DetailRow
                  label="Phone Number"
                  value={user?.phone || "N/A"}
                  isLink="tel"
                />
                <DetailRow label="Role" value={user?.role || "Customer"} />
                <DetailRow label="City" value={user?.city || "N/A"} />
                <DetailRow label="State" value={user?.state || "N/A"} />
                <DetailRow label="Zip Code" value={user?.zipCode || "N/A"} />
                {user?.createdAt && (
                  <DetailRow
                    label="Member Since"
                    value={formatDateOnly(user.createdAt)}
                  />
                )}
              </>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3, textAlign: "center" }}
              >
                No customer user profile linked.
              </Typography>
            )}
          </ModernCard>
        )}

        {/* TAB 3: CONTACT & LOCATION */}
        {activeTab === 3 && (
          <ModernCard title="Contact & Service Location" icon={<LocationOn />}>
            <DetailRow label="Contact Person" value={contactName} />
            <DetailRow
              label="Contact Phone"
              value={request?.phoneNumber || user?.phone || "N/A"}
              isLink="tel"
            />
            <DetailRow
              label="Contact Email"
              value={request?.email || user?.email || "N/A"}
              isLink="mailto"
            />
            <DetailRow label="Address" value={request?.address || "N/A"} />
            <DetailRow
              label="City"
              value={request?.city || user?.city || "N/A"}
            />
            <DetailRow
              label="State"
              value={request?.state || user?.state || "N/A"}
            />
            <DetailRow
              label="Zip Code"
              value={request?.zipCode || user?.zipCode || "N/A"}
            />
          </ModernCard>
        )}

        {/* TAB 4: ASSIGNMENT DETAILS */}
        {activeTab === 4 && (
          <ModernCard title="Assignment Details" icon={<SupportAgent />}>
            <DetailRow
              label="Assignment Type"
              value={
                <Chip
                  label={assignmentType}
                  size="small"
                  variant="outlined"
                  color={hasAgent || hasSalesperson ? "primary" : "default"}
                  sx={{ fontWeight: 600, fontSize: "0.78rem" }}
                />
              }
            />

            {hasAgent && (
              <>
                <DetailRow
                  label="Assigned Agent"
                  value={
                    [assignedAgent?.firstName, assignedAgent?.lastName]
                      .filter(Boolean)
                      .join(" ") ||
                    assignedAgent?.name ||
                    "N/A"
                  }
                />
                {assignedAgent?.phone && (
                  <DetailRow
                    label="Agent Phone"
                    value={assignedAgent.phone}
                    isLink="tel"
                  />
                )}
                {assignedAgent?.email && (
                  <DetailRow
                    label="Agent Email"
                    value={assignedAgent.email}
                    isLink="mailto"
                  />
                )}
              </>
            )}

            {hasSalesperson && (
              <>
                <DetailRow
                  label="Assigned Salesperson"
                  value={
                    [
                      assignedSalesperson?.firstName,
                      assignedSalesperson?.lastName,
                    ]
                      .filter(Boolean)
                      .join(" ") ||
                    assignedSalesperson?.name ||
                    "N/A"
                  }
                />

                {assignedSalesperson?.phone && (
                  <DetailRow
                    label="Salesperson Phone"
                    value={assignedSalesperson.phone}
                    isLink="tel"
                  />
                )}
                {assignedSalesperson?.email && (
                  <DetailRow
                    label="Salesperson Email"
                    value={assignedSalesperson.email}
                    isLink="mailto"
                  />
                )}
              </>
            )}

            {!hasAgent && !hasSalesperson && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3, textAlign: "center" }}
              >
                No agent or salesperson currently assigned to this request.
              </Typography>
            )}

            {approvedByName && (
              <DetailRow label="Approved By" value={approvedByName} />
            )}

            {request?.approvedAt && (
              <DetailRow
                label="Approved At"
                value={formatDate(request.approvedAt)}
              />
            )}
          </ModernCard>
        )}
      </Box>

      {/* ------------------------------------------------------------- */}
      {/* APPROVE SELL REQUEST CONFIRMATION POPUP */}
      {/* ------------------------------------------------------------- */}
      <Dialog
        open={openApproveModal}
        onClose={() => !approveLoading && setOpenApproveModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: theme.palette.success.main,
                  width: 40,
                  height: 40,
                }}
              >
                <CheckCircle sx={{ fontSize: 24 }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Approve Sell Request
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Make car available for sale
                </Typography>
              </Box>
            </Stack>
            <IconButton
              disabled={approveLoading}
              onClick={() => setOpenApproveModal(false)}
              size="small"
            >
              <CancelOutlined sx={{ fontSize: 20 }} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5 }}>
          <DialogContentText
            sx={{
              mb: 2,
              color: theme.palette.text.secondary,
              fontSize: "0.9rem",
              lineHeight: 1.5,
            }}
          >
            Are you sure you want to approve this sell car request? Once approved, the car will be marked as available for sale in the inventory.
          </DialogContentText>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 1.5,
              backgroundColor: theme.palette.grey[50],
              border: `1px solid #E0E0E0`,
            }}
          >
            <Stack spacing={1.2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                  Request ID:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                  {request?.requestId || `#${id}`}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                  Car:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                  {[carBrandName, carModelName, carMfgYear ? `(${carMfgYear})` : ""].filter(Boolean).join(" ") || "N/A"}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                  Customer:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                  {customerName}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                  Price / Valuation:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily, color: theme.palette.primary.main, fontWeight: 700 }}>
                  {formatCurrency(carPrice)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                  Assigned Agent:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                  {[assignedAgent?.firstName, assignedAgent?.lastName].filter(Boolean).join(" ") || assignedAgent?.name || "Assigned"}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => setOpenApproveModal(false)}
            disabled={approveLoading}
            sx={{
              flex: 1,
              borderRadius: 1,
              textTransform: "none",
              fontFamily,
              fontWeight: 600,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              "&:hover": {
                borderColor: theme.palette.primary.dark,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApproveSubmit}
            disabled={approveLoading}
            startIcon={approveLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
            sx={{
              flex: 1,
              borderRadius: 1,
              textTransform: "none",
              fontFamily,
              fontWeight: 600,
              backgroundColor: theme.palette.primary.main,
              color: "white",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {approveLoading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------- */}
      {/* DELETE REQUEST CONFIRMATION POPUP */}
      {/* ------------------------------------------------------------- */}
      <Dialog
        open={openDeleteModal}
        onClose={() => !isDeleting && setOpenDeleteModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "#101828",
              }}
            >
              Delete Request
            </Typography>
            <IconButton
              disabled={isDeleting}
              onClick={() => setOpenDeleteModal(false)}
              size="small"
              sx={{ color: theme.palette.grey[500] }}
            >
              <CancelOutlined style={{ fontSize: 20 }} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText
            sx={{
              mb: 2,
              fontFamily,
              color: "#474747",
              fontSize: "0.9rem",
              lineHeight: 1.5,
            }}
          >
            Are you sure you want to delete this request? This action cannot be undone.
          </DialogContentText>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 1.5,
              backgroundColor: theme.palette.grey[50],
              border: `1px solid #E0E0E0`,
            }}
          >
            <Stack spacing={1.2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                  Request ID:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                  {request?.requestId || `#${id}`}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                  Customer Name:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                  {customerName}
                </Typography>
              </Stack>
              {(carBrandName || carModelName) && (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                    Car Model:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                    {[carBrandName, carModelName].filter(Boolean).join(" ")}
                  </Typography>
                </Stack>
              )}
              {request?.type && (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                    Request Type:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                    {request.type}
                  </Typography>
                </Stack>
              )}
              {request?.status && (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                    Status:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily,
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                    }}
                  >
                    {request.status}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Paper>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => setOpenDeleteModal(false)}
            disabled={isDeleting}
            sx={{
              flex: 1,
              borderRadius: 1,
              textTransform: "none",
              fontFamily,
              fontWeight: 600,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              "&:hover": {
                borderColor: theme.palette.primary.dark,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (id) {
                dispatch(deleteRequest({ id: Number(id) }));
              }
            }}
            disabled={isDeleting}
            startIcon={
              isDeleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutline />
              )
            }
            sx={{
              flex: 1,
              borderRadius: 1,
              textTransform: "none",
              fontFamily,
              fontWeight: 600,
              backgroundColor: theme.palette.primary.main,
              color: "white",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequestDetailPage;
